import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { money } from '../../utils/region';
import { useReadOnlySeat } from '../../utils/readOnlySeat';

// ── Fixed assets ────────────────────────────────────────────────────────────────────────────────
// Two rules meet on this screen and they are not the same one. Revenue Code s.65 bis (2) says how
// much depreciation is DEDUCTIBLE — only at the decree's rates, prorated by the days the asset was
// held. DBD 2544 ข้อ 3.3(4) says the books must show each asset's increase and decrease, whether or
// not anything is deductible. So the rate is capped by the class, and the register prints movement.
//
// The maximum rates are reference data carried with their source, because they were transcribed from
// a summary of the decree rather than from the decree itself. The screen says so.

type Cls = { code: string; name_th: string; name_en: string; max_rate: number | null; depreciable: number; source: string; note: string };
// ── The client's own accounting categories (batch 5 round 8) ───────────────────────────────────
// ⛔ Two layers, and the screen says which is which: `decree_class_code` is the Royal Decree's tax
// class, which is where the rate CEILING comes from and nothing else; the category is the client's
// own grouping and names the three accounts its assets post to. Registering copies both onto the
// asset, so editing a category never moves an asset already registered.
type Cat = { id: string; name_th: string; name_en: string; decree_class_code: string; nature: string;
  asset_account_id: string; accum_account_id: string; expense_account_id: string;
  default_method: string; default_rate: number | null; active: number; assets: number;
  class_th: string; class_en: string; max_rate: number | null; depreciable: number;
  asset_account: string; accum_account: string; expense_account: string };
type Asset = { id: string; asset_no: string; name_th: string; name_en: string; class_th: string; class_en: string;
  acquired_date: string; cost: number; salvage: number; rate: number; accumulated: number; nbv: number;
  disposed_date: string; depreciable: number; category_id: string; category_th: string; category_en: string };
type Row = { assetId: string; assetNo: string; name: string; days: number; amount: number; chargedThisPeriod: number; skipped: string };
type Account = { id: string; code: string; name_th: string; name_en: string; class: string };

const dmy = (iso: string) => { const p = String(iso || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };
const baht = (s: number) => money(Number(s || 0) / 100);
const satang = (v: string) => Math.round((parseFloat(String(v).replace(/,/g, '')) || 0) * 100);

const btn: React.CSSProperties = {
  padding: '6px 11px', fontSize: 12, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};
/** The kinds of charge a category may be (round 9 adds the third). ⛔ `none` is LAND: held, never
 *  charged, so it has no accumulated account, no expense account and no rate — and the server checks
 *  that from both sides, because "never charged" on a 20%-a-year class would quietly stop charging
 *  assets the decree expects to be charged. */
const NATURES = ['depreciation', 'amortisation', 'none'] as const;

const inp: React.CSSProperties = {
  padding: '6px 9px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
};

/** The importer's fields (services/assetImport.ts FIELDS) in the reader's words — the SAME keys the
 *  workspace's import window names them with (panes/fixedAssets/ImportWindow.tsx FIELD_KEY), so one
 *  file reads the same on both doors. A field this page does not know yet shows its heading alone,
 *  never its code. */
const IMPORT_FIELD_KEY: Record<string, string> = {
  nameTh: 'aw_far_imp_f_name_th', nameEn: 'aw_far_imp_f_name_en', classCode: 'aw_far_imp_f_class', category: 'aw_far_imp_f_category',
  acquiredDate: 'aw_far_imp_f_acquired', cost: 'aw_far_imp_f_cost', salvage: 'aw_far_imp_f_salvage', rate: 'aw_far_imp_f_rate',
  assetAccount: 'aw_far_imp_f_asset_account', accumAccount: 'aw_far_imp_f_accum_account', expenseAccount: 'aw_far_imp_f_expense_account',
  openingAccum: 'aw_far_imp_f_opening_accum', openingAsOf: 'aw_far_imp_f_opening_as_of', note: 'aw_far_imp_f_note',
  vendorName: 'aw_far_imp_f_vendor', vendorInvoiceNo: 'aw_far_imp_f_invoice_no', capitalisedCost: 'aw_far_imp_f_capitalised',
  availableDate: 'aw_far_imp_f_available', depreciationStart: 'aw_far_imp_f_dep_start', location: 'aw_far_imp_f_location',
  status: 'aw_far_imp_f_status', usefulLifeYears: 'aw_far_imp_f_life',
};
const ignoredBox: React.CSSProperties = {
  padding: '9px 11px', marginBottom: 8, borderRadius: 7, background: '#FFFBEB', border: '1px solid #F1C232', color: '#92400E',
};
const readPill: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 999, whiteSpace: 'nowrap', display: 'inline-block',
  color: 'var(--text-tertiary)', background: 'var(--bg-app)', border: '1px solid var(--border)',
};

export default function FixedAssets({ clientId, periodId, periodClosed, onChanged }:
  { clientId: string; periodId: string; periodClosed: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  // ⛔ Who may change anything on this card: not a read-only seat (a client's own books login, the
  // auditor) and not a closed period — the classic twin of the workspace's canWriteIn
  // (panes/fixedAssets/shared.ts). A read-only seat keeps every read: the two lists, the month it
  // reads the charge for, and the statutory register PDF. Each write it was shown was a call the
  // server refuses (middleware.ts), and every refusal pages the admins.
  const readOnly = useReadOnlySeat();
  const canWrite = !periodClosed && !readOnly;
  const [classes, setClasses] = useState<Cls[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const [f, setF] = useState({ categoryId: '', classCode: 'other', nameTh: '', nameEn: '', acquiredDate: '', cost: '', salvage: '', rate: '',
    assetAccountId: '', accumAccountId: '', expenseAccountId: '', openingAccum: '', openingAsOf: '' });

  // ── Categories (batch 5 round 8, H2a) ──────────────────────────────────────────────────────
  // Loaded with the ones switched off included, because this pane has to be able to switch one back
  // on; the registration dropdown below filters them out, so a category switched off stops being
  // offered without disappearing from the list that manages it.
  const [cats, setCats] = useState<Cat[]>([]);
  const [catsOpen, setCatsOpen] = useState(false);
  const [catForm, setCatForm] = useState<null | { id: string; nameTh: string; nameEn: string; decreeClassCode: string;
    nature: string; defaultRate: string; assetAccountId: string; accumAccountId: string; expenseAccountId: string }>(null);
  const loadCats = useCallback(async () => {
    try { const d: any = await api.faCategories(clientId, true); setCats(d.categories || []); }
    catch { setCats([]); }
  }, [clientId]);
  useEffect(() => { loadCats(); }, [loadCats]);

  // ── The cadence this client's books charge on (owner 2026-10-16) ────────────────────────────
  // s.65 bis (2) sets the rate and the proration, not how often the charge is written down, so a
  // company that posts monthly and one that posts at the year end are both correct. The month is
  // only asked for — and only sent — when the client is monthly.
  const [cadence, setCadence] = useState<'annual' | 'monthly'>('annual');
  const [ym, setYm] = useState('');
  const load = useCallback(async () => {
    try {
      const [a, d]: any[] = await Promise.all([api.faAssets(clientId), api.faDepreciation(clientId, periodId, ym)]);
      setAssets(a.assets || []); setRows(d.rows || []);
      if (d.cadence) setCadence(d.cadence);
    } catch (e: any) { setErr(e.message); setAssets([]); setRows([]); }
  }, [clientId, periodId, ym]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.faClasses().then((d: any) => setClasses(d.classes || [])).catch(() => setClasses([]));
    // The accounts the three pickers offer. ⛔ Batch 8 R1 (design #24): nothing is pre-filled from them — the form used to
    // start the three pickers on the accounts coded 1500 / 1590 / 5600 of the house chart, a guess by code that is wrong
    // on any chart that numbers them otherwise. The asset's CATEGORY (required, COA-H3) carries its three accounts and
    // fills them in when it is picked (pickCategory below); until then the pickers are empty.
    api.jnlAccounts(clientId).then((d: any) => setAccounts(d.accounts || [])).catch(() => setAccounts([]));
  }, [clientId]);

  const cls = classes.find(c => c.code === f.classCode);
  const due = (rows || []).filter(r => !r.skipped && r.amount > 0 && !r.chargedThisPeriod);
  const dueTotal = due.reduce((s, r) => s + r.amount, 0);

  // Only the categories still in use are offered for a new asset.
  const liveCats = cats.filter(c => Number(c.active));
  /** Choosing a category fills the form in from it. ⛔ Prefill, not lock: the server starts from the
   *  category's accounts and rate, and a single asset may still be given its own (owner O13 default),
   *  so what is shown here is exactly what will be stored and it can be edited before saving. */
  const pickCategory = (id: string) => {
    const c = cats.find(x => x.id === id);
    if (!c) { setF(x => ({ ...x, categoryId: '' })); return; }
    setF(x => ({
      ...x, categoryId: c.id, classCode: c.decree_class_code,
      rate: c.default_rate == null ? '' : String(c.default_rate),
      assetAccountId: c.asset_account_id, accumAccountId: c.accum_account_id, expenseAccountId: c.expense_account_id,
    }));
  };

  // ⛔ The decree class and the kind of charge are ONE choice made two ways, so each follows the
  // other: picking a class the decree does not depreciate makes the category one that is never
  // charged, and picking "never charged" moves it onto such a class. The server refuses the two
  // apart, and a form that let them drift would just collect that refusal.
  const neverCharged = (code: string) => { const c = classes.find(x => x.code === code); return !!c && !Number(c.depreciable); };
  const firstNever = () => String(classes.find(c => !Number(c.depreciable))?.code || '');
  const catNever = catForm ? catForm.nature === 'none' : false;

  const saveCat = async () => {
    if (!catForm) return;
    setBusy('cat'); setErr(''); setMsg('');
    const never = catForm.nature === 'none';
    const body = {
      nameTh: catForm.nameTh, nameEn: catForm.nameEn, decreeClassCode: catForm.decreeClassCode,
      nature: catForm.nature,
      // Land carries no rate and no two accounts at all — '' and null, not a hidden leftover.
      defaultRate: never || catForm.defaultRate === '' ? null : Number(catForm.defaultRate),
      assetAccountId: catForm.assetAccountId,
      accumAccountId: never ? '' : catForm.accumAccountId,
      expenseAccountId: never ? '' : catForm.expenseAccountId,
    };
    try {
      if (catForm.id) await api.faUpdateCategory(catForm.id, body);
      else await api.faCreateCategory(clientId, body);
      setMsg(t('pa_cat_saved')); setCatForm(null); await loadCats();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  const toggleCat = async (c: Cat) => {
    setBusy('cat'); setErr(''); setMsg('');
    try { await api.faUpdateCategory(c.id, { active: !Number(c.active) }); await loadCats(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  // ⛔ Asks first, and the server refuses it anyway while any asset — disposed ones included — was
  // ever registered under it: the category is the only record of why those assets sit where they do.
  const delCat = async (c: Cat) => {
    if (!window.confirm(t('pa_cat_delete_confirm', { name: c.name_th }))) return;
    setBusy('cat'); setErr(''); setMsg('');
    try { await api.faDeleteCategory(c.id); await loadCats(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  const save = async () => {
    setBusy('save'); setErr(''); setMsg('');
    try {
      const r: any = await api.faRegister(clientId, {
        ...f, cost: satang(f.cost), salvage: satang(f.salvage), rate: f.rate ? Number(f.rate) : undefined,
        // Satang, like every other amount on this body (batch 9 R2). It used to be sent in baht and
        // crossed on the server, which left one request carrying two units.
        openingAccum: satang(f.openingAccum), openingAsOf: f.openingAsOf,
      });
      setMsg(t('pa_assets_registered_msg', { no: r.assetNo, rate: r.rate }));
      setF(x => ({ ...x, nameTh: '', nameEn: '', acquiredDate: '', cost: '', salvage: '', rate: '', openingAccum: '', openingAsOf: '' }));
      setAdding(false); await load();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  // ⛔ Preview, then commit — the same file, the same checks, and the first call writes nothing.
  // A register is loaded once and lived with for years; nobody should commit one blind. The server
  // refuses the whole file if any row is refused, so what the preview shows is what the commit does.
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any>(null);
  // services/assetImport.ts IgnoredColumn; `repeats` is the field an earlier column already fills.
  // `|| []`: a server older than this page does not send it.
  const importIgnored: { column: number; header: string; repeats: string }[] = preview?.ignored || [];
  const importFieldName = (k: string) => (IMPORT_FIELD_KEY[k] ? t(IMPORT_FIELD_KEY[k]) : '');
  const runImport = async (commit: boolean) => {
    if (!file) return;
    setBusy('import'); setErr(''); setMsg('');
    try {
      const r: any = await api.faImportAssets(clientId, file, !commit, f.categoryId);
      setPreview(r);
      if (commit) {
        setMsg(t('pa_assets_import_done', { n: r.created }));
        setFile(null); setPreview(null); setImporting(false);
        await load(); onChanged();
      }
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  const postDep = async () => {
    // ⛔ Ask first. Charging depreciation writes a dated entry against every due asset at once, and
    // the only way back is to discard the draft — so the one irreversible-feeling button on this
    // screen should not fire on a stray click. It also says what the button does NOT do: the draft
    // still has to be posted under Journal entries before any of it reaches the ledger.
    if (!window.confirm(t('pa_assets_dep_confirm', { amount: baht(dueTotal) }))) return;
    setBusy('dep'); setErr(''); setMsg('');
    try {
      const r: any = await api.faPostDepreciation(clientId, periodId, cadence === 'monthly' ? ym : '');
      setMsg(r.assets ? t('pa_assets_dep_draft', { n: r.assets, amount: baht(r.amount) }) : t('pa_assets_dep_none'));
      await load(); onChanged();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  const openRegister = async () => {
    setBusy('reg'); setErr('');
    try {
      const url = await api.faRegisterUrl(periodId);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  if (assets === null) return null;

  const catAcctSelect = (k: 'assetAccountId' | 'accumAccountId' | 'expenseAccountId', label: string) => (
    <label key={k} style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t(label)}<br />
      <select value={catForm ? (catForm as any)[k] : ''} onChange={e => setCatForm(c => c && ({ ...c, [k]: e.target.value }))}
        data-cat-field={k} style={{ ...inp, width: 230 }}>
        <option value="">—</option>
        {accounts.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name_th}</option>)}
      </select></label>
  );
  const catCls = classes.find(c => c.code === (catForm?.decreeClassCode || ''));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    {/* ── Categories ────────────────────────────────────────────────────────────────────────────
        The client's own accounting groupings, each pointing at one of the decree's tax classes for
        its rate ceiling and naming the three accounts its assets post to. Nothing is pre-seeded for
        anybody: which categories a client should have is the owner's open question, and an asset can
        still be registered without one. */}
    <div data-section="asset-categories" style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', flexWrap: 'wrap' }}>
        <i className="ti ti-category" style={{ fontSize: 18, color: 'var(--accent)' }} aria-hidden />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{t('pa_cat_title')}</span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }} data-cat-summary>
            {cats.length ? t('pa_cat_count', { n: cats.length }) : t('pa_cat_none')}
          </span>
        </span>
        {canWrite && <button onClick={() => { setCatsOpen(true); setErr(''); setMsg(''); setCatForm({ id: '', nameTh: '', nameEn: '', decreeClassCode: 'other', nature: 'depreciation', defaultRate: '', assetAccountId: '', accumAccountId: '', expenseAccountId: '' }); }}
          data-cat-add style={btn}><i className="ti ti-plus" aria-hidden /> {t('pa_cat_add')}</button>}
        {!!cats.length && <button onClick={() => setCatsOpen(o => !o)} data-cat-toggle style={btn}>{catsOpen ? t('pa_assets_hide') : t('pa_assets_open')}</button>}
      </div>

      {catForm && !readOnly && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '11px 13px 13px' }} data-cat-form>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_cat_f_class')}<br />
              <select value={catForm.decreeClassCode}
                onChange={e => setCatForm(c => c && ({ ...c, decreeClassCode: e.target.value, defaultRate: '',
                  nature: neverCharged(e.target.value) ? 'none' : (c.nature === 'none' ? 'depreciation' : c.nature) }))}
                data-cat-field="decreeClassCode" style={{ ...inp, width: 250 }}>
                {classes.map(c => <option key={c.code} value={c.code}>{c.name_th} — {c.name_en}{c.max_rate != null ? ` (≤${c.max_rate}%)` : ` (${t('pa_assets_no_dep')})`}</option>)}
              </select></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_cat_f_nature')}<br />
              <select value={catForm.nature}
                onChange={e => setCatForm(c => c && ({ ...c, nature: e.target.value,
                  decreeClassCode: e.target.value === 'none'
                    ? (neverCharged(c.decreeClassCode) ? c.decreeClassCode : firstNever())
                    : (neverCharged(c.decreeClassCode) ? 'other' : c.decreeClassCode),
                  defaultRate: e.target.value === 'none' ? '' : c.defaultRate }))}
                data-cat-field="nature" style={{ ...inp, width: 190 }}>
                {NATURES.map(n => <option key={n} value={n}>{t(`pa_cat_nature_${n}`)}</option>)}
              </select></label>
            {!catNever && (
              <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_cat_f_rate')} {catCls?.max_rate != null && <span style={{ color: 'var(--text-tertiary)' }}>(≤{catCls.max_rate})</span>}<br />
                <input value={catForm.defaultRate} onChange={e => setCatForm(c => c && ({ ...c, defaultRate: e.target.value }))}
                  data-cat-field="defaultRate" inputMode="decimal" placeholder={catCls?.max_rate != null ? String(catCls.max_rate) : '—'}
                  style={{ ...inp, width: 90, textAlign: 'right' }} /></label>
            )}
          </div>
          <input value={catForm.nameTh} onChange={e => setCatForm(c => c && ({ ...c, nameTh: e.target.value }))}
            data-cat-field="nameTh" placeholder={t('pa_cat_f_name_th')} style={{ ...inp, width: '100%', marginBottom: 6 }} />
          <input value={catForm.nameEn} onChange={e => setCatForm(c => c && ({ ...c, nameEn: e.target.value }))}
            data-cat-field="nameEn" placeholder={t('pa_cat_f_name_en')} style={{ ...inp, width: '100%', marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 9 }}>
            {catAcctSelect('assetAccountId', 'pa_assets_f_acct_asset')}
            {/* ⛔ Not shown, and not sent, for a category that is never charged: land has nothing to
                accumulate and nothing to charge, so there is no account for either. */}
            {!catNever && catAcctSelect('accumAccountId', 'pa_assets_f_acct_accum')}
            {!catNever && catAcctSelect('expenseAccountId', 'pa_assets_f_acct_expense')}
          </div>
          {catNever && <div data-cat-never style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 8, lineHeight: 1.6 }}>{t('pa_cat_never_note')}</div>}
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 8, lineHeight: 1.6 }}>{t('pa_cat_note')}</div>
          <div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end' }}>
            <button onClick={() => setCatForm(null)} style={btn}>{t('pa_assets_cancel')}</button>
            <button onClick={saveCat} disabled={!!busy} data-cat-save style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }}>
              <i className={busy === 'cat' ? 'ti ti-loader' : 'ti ti-device-floppy'} aria-hidden /> {t('pa_assets_submit')}
            </button>
          </div>
        </div>
      )}

      {catsOpen && !!cats.length && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 13px 13px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
              <th style={{ textAlign: 'left', padding: '0 6px 5px 0' }}>{t('pa_cat_th_name')}</th>
              <th style={{ textAlign: 'left', padding: '0 6px 5px' }}>{t('pa_cat_th_class')}</th>
              <th style={{ textAlign: 'left', padding: '0 6px 5px' }}>{t('pa_cat_th_accounts')}</th>
              <th style={{ textAlign: 'right', padding: '0 6px 5px' }}>{t('pa_cat_th_assets')}</th>
              <th style={{ textAlign: 'right', padding: '0 0 5px 6px' }} />
            </tr></thead>
            <tbody>
              {cats.map(c => (
                <tr key={c.id} data-cat-row={c.id} style={{ borderTop: '1px solid var(--border)', opacity: Number(c.active) ? 1 : 0.55 }}>
                  <td style={{ padding: '6px 6px 6px 0' }}>
                    <span style={{ fontWeight: 700 }}>{c.name_th}</span>
                    <span style={{ color: 'var(--text-tertiary)' }}> {c.name_en}</span><br />
                    <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>
                      {t(`pa_cat_nature_${c.nature}`)}{Number(c.active) ? '' : ` · ${t('pa_cat_inactive')}`}
                    </span>
                  </td>
                  <td style={{ padding: '6px', color: 'var(--text-secondary)' }}>
                    {c.class_th}<br />
                    <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>
                      {c.default_rate == null ? t('pa_assets_no_dep') : `${c.default_rate}%`}
                      {c.max_rate != null ? ` (≤${c.max_rate}%)` : ''}
                    </span>
                  </td>
                  <td style={{ padding: '6px', fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                    {[c.asset_account, c.accum_account, c.expense_account].filter(Boolean).map((a, i) => <div key={i}>{a}</div>)}
                  </td>
                  <td style={{ padding: '6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.assets || ''}</td>
                  <td style={{ padding: '6px 0 6px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {canWrite && <>
                      <button onClick={() => { setErr(''); setMsg(''); setCatForm({ id: c.id, nameTh: c.name_th, nameEn: c.name_en || '',
                        decreeClassCode: c.decree_class_code, nature: c.nature, defaultRate: c.default_rate == null ? '' : String(c.default_rate),
                        assetAccountId: c.asset_account_id || '', accumAccountId: c.accum_account_id || '', expenseAccountId: c.expense_account_id || '' }); }}
                        data-cat-edit={c.id} style={{ ...btn, padding: '3px 8px', fontSize: 11 }}>{t('pa_cat_edit')}</button>{' '}
                      <button onClick={() => toggleCat(c)} disabled={!!busy} data-cat-toggle-active={c.id}
                        style={{ ...btn, padding: '3px 8px', fontSize: 11 }}>{Number(c.active) ? t('pa_cat_off') : t('pa_cat_on')}</button>{' '}
                      {/* ⛔ Only while nothing was ever registered under it: the server refuses the rest, and
                          a refused call pages the admins. Switching it off is the way out of use. */}
                      {!Number(c.assets) && <button onClick={() => delCat(c)} disabled={!!busy} data-cat-delete={c.id}
                        style={{ ...btn, padding: '3px 8px', fontSize: 11 }}>{t('pa_cat_delete')}</button>}
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>

    <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', flexWrap: 'wrap' }}>
        <i className="ti ti-building-warehouse" style={{ fontSize: 18, color: 'var(--accent)' }} aria-hidden />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{t('pa_assets_register_name')} · {t('pa_assets_title')}</span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>
            {assets.length ? t('pa_assets_summary', { n: assets.length, nbv: baht(assets.reduce((s, a) => s + a.nbv, 0)) }) : t('pa_assets_empty')}
            {dueTotal ? ` · ${t('pa_assets_due', { amount: baht(dueTotal) })}` : ''}
          </span>
        </span>
        {!!assets.length && <button onClick={openRegister} disabled={!!busy} style={btn}>
          <i className={busy === 'reg' ? 'ti ti-loader' : 'ti ti-file-type-pdf'} aria-hidden /> {t('pa_assets_open_register')}</button>}
        {/* Annual or monthly, and which month. ⛔ The cadence cannot be changed once an open period
            already carries charges: twelve monthly rows beside one yearly row would double the
            year, and the server refuses it with that sentence. A read-only seat is told the cadence
            as text; the month box only chooses which month's charge is READ, so it keeps that. */}
        {!periodClosed && (
          <>
            {readOnly ? (
              <span data-fa-cadence-text style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {cadence === 'monthly' ? t('pa_assets_cadence_monthly') : t('pa_assets_cadence_annual')}
              </span>
            ) : <select value={cadence} disabled={!!busy}
              onChange={async e => {
                const next = e.target.value as 'annual' | 'monthly';
                setBusy('cad'); setErr('');
                try { const r: any = await api.faCadence(clientId, next); setCadence(r.cadence); setYm(''); await load(); }
                catch (err: any) { setErr(err.message); } finally { setBusy(''); }
              }}
              style={{ ...inp, width: 150 }}>
              <option value="annual">{t('pa_assets_cadence_annual')}</option>
              <option value="monthly">{t('pa_assets_cadence_monthly')}</option>
            </select>}
            {cadence === 'monthly' && (
              <input type="month" value={ym} onChange={e => setYm(e.target.value)} title={t('pa_assets_month')}
                style={{ ...inp, width: 140 }} />
            )}
          </>
        )}
        {canWrite && cadence === 'monthly' && !ym && <span style={{ fontSize: 11.5, color: '#B45309' }}>{t('pa_assets_pick_month')}</span>}
        {canWrite && !!dueTotal && (
          <button onClick={postDep} disabled={!!busy || (cadence === 'monthly' && !ym)} style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none', opacity: (cadence === 'monthly' && !ym) ? 0.5 : 1 }}>
            <i className={busy === 'dep' ? 'ti ti-loader' : 'ti ti-calculator'} aria-hidden /> {t('pa_assets_charge_dep', { amount: baht(dueTotal) })}
          </button>
        )}
        {canWrite && <button onClick={() => setAdding(a => !a)} style={{ ...btn, background: adding ? 'var(--bg-active)' : 'var(--bg-surface)' }}>
          <i className="ti ti-plus" aria-hidden /> {t('pa_assets_add')}</button>}
        {/* A whole register at once. Twenty assets is an afternoon of the form below; five hundred
            is why this exists (owner 2026-10-16). */}
        {canWrite && (
          <button onClick={() => { setImporting(v => !v); setAdding(false); setErr(''); setMsg(''); }}
            style={{ ...btn, background: importing ? 'var(--bg-active)' : 'var(--bg-surface)' }}>
            <i className="ti ti-file-upload" aria-hidden /> {t('pa_assets_import_btn')}
          </button>
        )}
        {!!assets.length && <button onClick={() => setOpen(o => !o)} style={btn}>{open ? t('pa_assets_hide') : t('pa_assets_open')}</button>}
      </div>

      {msg && <div style={{ fontSize: 12.5, color: '#166534', background: '#E8F6EE', borderTop: '1px solid #BBE5CB', padding: '8px 13px' }}>{msg}</div>}
      {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', borderTop: '1px solid #F3B9B9', padding: '8px 13px' }}>{err}</div>}

      {importing && !readOnly && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '11px 13px 13px' }}>
          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 8, lineHeight: 1.6 }}>
            {t('pa_assets_import_note')}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Batch 9 R3b: the category a row without its own Category column takes. */}
            <select value={f.categoryId} onChange={e => { pickCategory(e.target.value); setPreview(null); }} data-import-category style={{ ...inp, width: 230 }} title={t('pa_assets_import_category')}>
              <option value="">{t('pa_assets_import_category')}</option>
              {liveCats.map(c => <option key={c.id} value={c.id}>{c.name_th}{c.name_en ? ` — ${c.name_en}` : ''}</option>)}
            </select>
            <input type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); setErr(''); }} style={{ fontSize: 12 }} />
            <button onClick={() => runImport(false)} disabled={!file || !!busy} style={{ ...btn, opacity: (!file || busy) ? 0.5 : 1 }}>
              <i className={busy === 'import' ? 'ti ti-loader' : 'ti ti-eye'} aria-hidden /> {t('pa_assets_import_preview')}
            </button>
            <button onClick={() => runImport(true)} disabled={!file || !!busy || !preview || preview.refused > 0}
              style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none', opacity: (!file || busy || !preview || preview.refused > 0) ? 0.5 : 1 }}>
              <i className="ti ti-device-floppy" aria-hidden /> {t('pa_assets_import_commit')}
            </button>
          </div>
          {preview && (
            <div style={{ marginTop: 10, overflowX: 'auto' }}>
              {/* ⛔ Every column of the file accounted for, above the verdict (review R4 #15, follow-up):
                  this page posts to the same import door as the workspace's window and gets the same
                  report, so it shows the same two lists — the columns it will NOT read, in an amber box
                  nobody scrolls past, then the columns read, each with the field it fills. Drawing only
                  the rows let a file's 'Useful life (yrs)' be dropped without a word here too: every
                  row said "will be registered", and the commit used the category's rate instead. */}
              {importIgnored.length > 0 && (
                <div style={ignoredBox} data-pa-imp="ignored">
                  <div style={{ fontSize: 12, fontWeight: 800 }}><i className="ti ti-alert-triangle" aria-hidden /> {t('pa_assets_import_ignored', { n: importIgnored.length })}</div>
                  <ul style={{ margin: '5px 0 0', paddingLeft: 20, fontSize: 11.5, lineHeight: 1.6 }}>
                    {importIgnored.map(c => (
                      <li key={c.column} data-pa-imp-ignored={c.column}>
                        {t('aw_far_imp_column_n', { n: c.column })} <b>{c.header || t('aw_far_imp_no_heading')}</b>
                        {c.repeats && <> — {t('aw_far_imp_twice', { field: importFieldName(c.repeats) || c.header })}</>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center', fontSize: 11.5, color: 'var(--text-secondary)', marginBottom: 8 }} data-pa-imp="columns">
                <span style={{ fontWeight: 700 }}>{t('aw_far_imp_read')}</span>
                {Object.entries((preview.columns || {}) as Record<string, string>).map(([fk, h]) => {
                  const name = importFieldName(fk);
                  return (
                    <span key={fk} style={readPill} data-pa-imp-read={fk}>
                      {h}{name && name.toLowerCase() !== String(h).toLowerCase() ? ` → ${name}` : ''}
                    </span>
                  );
                })}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: preview.refused ? '#B42318' : '#166534', marginBottom: 6 }} data-pa-imp="summary">
                {t('pa_assets_import_summary', { ok: preview.ok, refused: preview.refused })}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <tbody>
                  {(preview.rows || []).slice(0, 100).map((r: any, i: number) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border)', background: r.error ? '#FEF2F2' : undefined }}>
                      <td style={{ padding: '6px 6px 6px 0', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{dmy(r.acquiredDate)}</td>
                      <td style={{ padding: '6px', fontWeight: 700 }}>{r.nameTh || r.nameEn}</td>
                      <td style={{ padding: '6px' }}>{r.classCode}</td>
                      <td style={{ padding: '6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{baht(r.cost)}</td>
                      <td style={{ padding: '6px', fontSize: 11.5, color: r.error ? '#B42318' : '#166534' }}>{r.error || t('pa_assets_import_will_add')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {adding && !readOnly && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '11px 13px 13px' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            {/* The client's own category, chosen first: it fills in the decree class, the rate and the
                three accounts. ⛔ Required (batch 9 R3b, COA-H3: "Each asset must be assigned to an
                asset category") — the server refuses a registration without one, so the form offers
                no "none" to choose. The empty option is only the prompt. */}
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_assets_f_category')}<br />
              <select value={f.categoryId} onChange={e => pickCategory(e.target.value)} data-asset-category style={{ ...inp, width: 250 }}>
                <option value="" disabled>{t('pa_assets_f_category_pick')}</option>
                {liveCats.map(c => <option key={c.id} value={c.id}>{c.name_th}{c.name_en ? ` — ${c.name_en}` : ''}</option>)}
              </select></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_assets_f_class')}<br />
              <select value={f.classCode} onChange={e => setF({ ...f, classCode: e.target.value, rate: '' })} style={{ ...inp, width: 250 }}>
                {classes.map(c => <option key={c.code} value={c.code}>{c.name_th} — {c.name_en}{c.max_rate != null ? ` (≤${c.max_rate}%)` : ` (${t('pa_assets_no_dep')})`}</option>)}
              </select></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_assets_f_acquired')}<br />
              <input type="date" value={f.acquiredDate} onChange={e => setF({ ...f, acquiredDate: e.target.value })} data-asset-field="acquiredDate" style={{ ...inp, width: 150 }} /></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_assets_f_cost')}<br />
              <input value={f.cost} onChange={e => setF({ ...f, cost: e.target.value })} inputMode="decimal" data-asset-field="cost" style={{ ...inp, width: 130, textAlign: 'right' }} /></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_assets_f_salvage')}<br />
              <input value={f.salvage} onChange={e => setF({ ...f, salvage: e.target.value })} inputMode="decimal" style={{ ...inp, width: 120, textAlign: 'right' }} /></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_assets_f_rate')} {cls?.max_rate != null && <span style={{ color: 'var(--text-tertiary)' }}>(≤{cls.max_rate})</span>}<br />
              <input value={f.rate} onChange={e => setF({ ...f, rate: e.target.value })} placeholder={cls?.max_rate != null ? String(cls.max_rate) : '—'}
                disabled={!cls?.depreciable} inputMode="decimal" data-asset-field="rate" style={{ ...inp, width: 90, textAlign: 'right' }} /></label>
          </div>
          {/* ── An asset that did not start its life here (owner 2026-10-16) ──────────────────
              A company migrating in has a three-year-old vehicle: the cost is the cost, but two
              thirds of it has already been charged in books this system never held. Left blank the
              register would charge the whole asset a second time, in balance and with nothing to
              show why. The date is required whenever the figure is given — the next charge starts
              the day after it, so the stretch already charged is never charged twice. */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8, alignItems: 'flex-end' }}>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_assets_f_opening')}<br />
              <input value={f.openingAccum} onChange={e => setF({ ...f, openingAccum: e.target.value })}
                inputMode="decimal" placeholder="0.00" style={{ ...inp, width: 150, textAlign: 'right' }} /></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_assets_f_opening_as_of')}<br />
              <input type="date" value={f.openingAsOf} onChange={e => setF({ ...f, openingAsOf: e.target.value })}
                style={{ ...inp, width: 150 }} /></label>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', flex: '1 1 260px', lineHeight: 1.5 }}>{t('pa_assets_opening_hint')}</div>
          </div>
          <input value={f.nameTh} onChange={e => setF({ ...f, nameTh: e.target.value })} data-asset-field="nameTh" placeholder={t('pa_assets_f_name_th')} style={{ ...inp, width: '100%', marginBottom: 6 }} />
          <input value={f.nameEn} onChange={e => setF({ ...f, nameEn: e.target.value })} data-asset-field="nameEn" placeholder={t('pa_assets_f_name_en')} style={{ ...inp, width: '100%', marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 9 }}>
            {([['assetAccountId', 'pa_assets_f_acct_asset'], ['accumAccountId', 'pa_assets_f_acct_accum'], ['expenseAccountId', 'pa_assets_f_acct_expense']] as const).map(([k, label]) => (
              <label key={k} style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t(label)}<br />
                <select value={(f as any)[k]} onChange={e => setF({ ...f, [k]: e.target.value })} data-asset-field={k} style={{ ...inp, width: 250 }}>
                  <option value="">—</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name_th}</option>)}
                </select></label>
            ))}
          </div>
          {cls && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 8, lineHeight: 1.6 }}>
            {cls.note && <div>{cls.note}</div>}
            <div>{t('pa_assets_max_rate_source', { source: cls.source })}</div>
          </div>}
          <div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end' }}>
            <button onClick={() => setAdding(false)} style={btn}>{t('pa_assets_cancel')}</button>
            <button onClick={save} disabled={!!busy || !f.categoryId} data-asset-save style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none', opacity: !f.categoryId ? 0.5 : 1 }}>
              <i className={busy === 'save' ? 'ti ti-loader' : 'ti ti-device-floppy'} aria-hidden /> {t('pa_assets_submit')}
            </button>
          </div>
        </div>
      )}

      {open && !!assets.length && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 13px 13px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
              <th style={{ textAlign: 'left', padding: '0 6px 5px 0' }}>{t('pa_assets_th_no')}</th>
              <th style={{ textAlign: 'left', padding: '0 6px 5px' }}>{t('pa_assets_th_item')}</th>
              <th style={{ textAlign: 'left', padding: '0 6px 5px' }}>{t('pa_assets_th_acquired')}</th>
              <th style={{ textAlign: 'right', padding: '0 6px 5px' }}>{t('pa_assets_th_cost')}</th>
              <th style={{ textAlign: 'right', padding: '0 6px 5px' }}>{t('pa_assets_th_accum')}</th>
              <th style={{ textAlign: 'right', padding: '0 6px 5px' }}>{t('pa_assets_th_nbv')}</th>
              <th style={{ textAlign: 'right', padding: '0 0 5px 6px' }}>{t('pa_assets_th_year')}</th>
            </tr></thead>
            <tbody>
              {assets.map(a => {
                const r = (rows || []).find(x => x.assetId === a.id);
                return (
                  <tr key={a.id} style={{ borderTop: '1px solid var(--border)', opacity: a.disposed_date ? 0.55 : 1 }}>
                    <td style={{ padding: '6px 6px 6px 0', whiteSpace: 'nowrap', fontWeight: 700 }}>{a.asset_no}</td>
                    <td style={{ padding: '6px' }}>
                      {a.name_th}<span style={{ color: 'var(--text-tertiary)' }}> {a.name_en}</span><br />
                      <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>
                        {/* The client's own category, when the asset was registered under one — then
                            the decree class it is bounded by, which is a different fact. */}
                        {a.category_th ? `${a.category_th} · ` : ''}
                        {a.class_th} · {Number(a.depreciable) ? `${a.rate}%` : t('pa_assets_no_dep')}
                        {a.disposed_date ? ` · ${t('pa_assets_disposed_on', { date: dmy(a.disposed_date) })}` : ''}
                      </span>
                    </td>
                    <td style={{ padding: '6px', whiteSpace: 'nowrap', color: 'var(--text-tertiary)' }}>{dmy(a.acquired_date)}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{baht(a.cost)}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>{baht(a.accumulated)}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{baht(a.nbv)}</td>
                    <td style={{ padding: '6px 0 6px 6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {/* What this period charges, and whether it has already been recorded — the two
                          are different questions and the screen answers both. */}
                      {r?.chargedThisPeriod ? <span style={{ color: '#166534' }}>{baht(r.chargedThisPeriod)} ✓</span>
                        : r?.skipped ? <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>{r.skipped}</span>
                        : r?.amount ? <span>{baht(r.amount)}<span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}> · {t('pa_assets_days', { n: r.days })}</span></span>
                        : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </div>
  );
}
